import {useMemo} from 'react'

import {ChangeableFields} from '../types'
import { normalizeFieldName } from '../../../../fields'

/**
 * Custom hook that applies spacing-specific business rules to changeable fields.
 * This hook modifies the base changeable field rules to handle special cases for
 * spacing and timing fields that can be auto-adjusted by the system.
 *
 * @param baseChangeableFields - Base changeable fields from useBaseChangeableFieldsRules
 * @returns Modified changeable fields with spacing-specific rules applied
 *
 * Business Logic:
 * - Spacing and timing fields that are 'notChangeable' but dirty (auto-adjusted)
 *   are promoted to 'autoConfirmable' to allow system corrections
 * - This handles cases where the system automatically adjusts spacing/timing
 *   after the normal changeable deadline has passed
 * - All other fields maintain their original state from base rules
 *
 * Use Cases:
 * - User changes quantity but doesn't provide flow rate or time on job site information
 *   System automatically adjusts these fields to default values, but we want user to
 *   confirm/correct these auto-generated values even after normal change deadlines
 */
export const useSpacingChangeableFieldsRules = (
  baseChangeableFields: ChangeableFields
): ChangeableFields =>
  useMemo(
    () =>
      baseChangeableFields.map((changeableField) => {
        const {fieldName, isDirty, state} = changeableField

        // Identify fields that have been auto-adjusted by the system
        // These are fields that are marked as dirty but not changeable
        const isAutoAdjusted = state === 'notChangeable' && isDirty

        // Apply special rules for spacing and timing fields
        // These fields can be auto-confirmed even after normal deadlines when system-adjusted
        if (['spacingOrFlowRate', 'timeOnJobSite'].includes(normalizeFieldName(fieldName).field) && isAutoAdjusted) {
          return {
            fieldName,
            isDirty,
            state: 'autoConfirmable' // Override: allow confirmation of system adjustments
          }
        }

        // Return unchanged for all other fields
        return changeableField
      }),
    [baseChangeableFields]
  )
